import { Grid, Menu, Typography } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { DefaultListParameters } from "assets/appConfig";
import { FeedStyleClasses, TimelineStyleClasses } from "assets/jss/common-styles";
import AdvFilter, { AdvFilterCommand, AdvFilterComponent, AdvFilterPicker, AdvFilterResult } from "components/AdvFilter";
import { CmdCreateEndDate, CmdCreateStartDate, CmdDescription, CmdTopicPrivate, CmdTopicPublic, CmdWithApprovals, CmdWithoutApprovals, CommandLabels } from "components/AdvFilter/CommonCommands";
import ButtonResetFilter from "components/Buttons/ButtonResetFilter";
import IconButtonAdd from "components/Buttons/IconButtonAdd";
import IconButtonHierarchy from "components/Buttons/IconButtonHierarchy";
import IconButtonOrderBy from "components/Buttons/IconButtonOrderby";
import { RequestIndicator } from "components/CallServer";
import { LoadingIndicator } from "components/CallServer/LoadingIndicator";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import CountFilter from "components/CountFilter";
import { MuiFilterAdvancedContainer, MuiFilterAppBar, MuiFilterLine, MuiFilterToolbar } from "components/MuiExtras/Filter";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiConfirmation from "components/MuiExtras/MuiConfirmation";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiMenuItemChecked from "components/MuiExtras/MuiMenuItemChecked";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiPushRight from "components/MuiExtras/MuiPushRight";
import MuiDanger from "components/MuiExtras/Typography/MuiDanger";
import MuiSuccess from "components/MuiExtras/Typography/MuiSuccess";
import MuiWarning from "components/MuiExtras/Typography/MuiWarning";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import AppMessage from "components/Translations/AppMessage";
import HtmlUtils from "lib/HtmlUtils";
import LibUtils, { nprop } from "lib/LibUtils";
import { DynamicSystemRoles, SystemRoles } from "models/ApiEnums";
import { GetTopicListAdminParams, PagedResult, TopicListAdminItemDto, TopicListAdminOrderBy, TopicListFilter, TopicListFilterVisibility } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import * as React from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router";
import { RoutePaths } from "routes/RouteConfig";
import Session from "services/Session";
import TopicCard from "views/Topic/Components/TopicCard";
import TopicEdit from "views/Topic/Components/TopicEdit";
import TopicJoinDialog from "views/Topic/Dialogs/TopicJoinDialog";
import TopicRelationshipsDialog from "views/Topic/Dialogs/TopicRelationshipsDialog";
import TopicTreeDialog from "views/Topic/Dialogs/TopicTreeDialog";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    advFilters: AdvFilterResult[];
    searchText: string;

    orderByMenuAnchorEl: any;
    dialog: any;
    list: PagedResult<TopicListAdminItemDto>;
    offset: number;
    formCycle: RequestLifeCycle;
    lastError: any;
    editTopicId: string;
}

interface IProps {
}

interface TopicAdminRouteProps {
    id: string;
}

export interface TopicAdminRouteStateProps {
    reloadKey: string;
}

type Props = IProps & RouteComponentProps<TopicAdminRouteProps, {}, TopicAdminRouteStateProps> & WithStyles<typeof TimelineStyleClasses> & WithStyles<typeof FeedStyleClasses> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////

export class TopicAdminList extends React.Component<Props, IState> {

    private advFilterRef: AdvFilterComponent;
    private orderBy: TopicListAdminOrderBy = TopicListAdminOrderBy.Title;
    private isFiltered: boolean;

    ///
    constructor(props: Props) {
        super(props);

        this.state = {
            advFilters: [],
            searchText: "",

            list: { items: null, total: 0 },
            offset: 0,
            orderByMenuAnchorEl: null,
            formCycle: RequestLifeCycle.None,
            lastError: null,
            dialog: null,
            editTopicId: this.props.match.params.id,
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Topic.TopicAdminList." + r);
    }

    private getCommands(): AdvFilterCommand[] {
        const labels = CommandLabels();

        const cmds: AdvFilterCommand[] = [
            { keyword: CmdCreateStartDate, displayName: labels[CmdCreateStartDate], picker: AdvFilterPicker.Calendar, group: 1 },
            { keyword: CmdCreateEndDate, displayName: labels[CmdCreateEndDate], picker: AdvFilterPicker.Calendar, group: 1 },
            { keyword: CmdWithApprovals, displayName: labels[CmdWithApprovals], group: 2 },
            { keyword: CmdWithoutApprovals, displayName: labels[CmdWithoutApprovals], group: 2 },
            { keyword: CmdDescription, displayName: labels[CmdDescription], picker: AdvFilterPicker.Text, group: 3 },
            { keyword: CmdTopicPublic, displayName: labels[CmdTopicPublic], group: 4, optionGroup: 4 },
            { keyword: CmdTopicPrivate, displayName: labels[CmdTopicPrivate], group: 4, optionGroup: 4 },
        ];

        return cmds;
    }

    ///
    private loadData(offset: number) {

        const p: GetTopicListAdminParams = {
            filter: this.getFilter(),
            orderBy: this.orderBy,
            range: { offset: offset, take: DefaultListParameters.viewRecordsByPage }
        };

        this.setState({ formCycle: RequestLifeCycle.Loading, orderByMenuAnchorEl: null });

        api.getTopicAdminList(p)
            .then((result) => {
                this.setState({ list: result, formCycle: RequestLifeCycle.None, offset: offset });
            })
            .catch((error) => {
                this.setState({ list: null, lastError: error, offset: 0, formCycle: RequestLifeCycle.LoadFailed });
            });
    }

    //#region FILTER

    ///
    private getFilter(): TopicListFilter {
        const { searchText, advFilters } = this.state;

        if (searchText.length == 0 && advFilters.length == 0) {
            this.isFiltered = false;
            return {
                searchText: null,
                startDate: null,
                endDate: null,
                description: null,
                withApproval: null,
                withoutApproval: null,
                visibility: null
            };
        }
        else {
            this.isFiltered = true;

            var dts = advFilters.find(x => x.keyword == CmdCreateStartDate);
            var dte = advFilters.find(x => x.keyword == CmdCreateEndDate);

            return {
                searchText: searchText.length == 0 ? null : searchText,
                startDate: dts ? (dts.value as Date).putStartOfDay() : null,
                endDate: dte ? (dte.value as Date).putEndOfDay() : null,
                withApproval: advFilters.findIndex(x => x.keyword == CmdWithApprovals) > -1,
                withoutApproval: advFilters.findIndex(x => x.keyword == CmdWithoutApprovals) > -1,
                description: nprop(nprop(advFilters.find(x => x.keyword == CmdDescription)).value, null),
                visibility: advFilters.findIndex(x => x.keyword == CmdTopicPublic) > -1
                    ? TopicListFilterVisibility.Public
                    : advFilters.findIndex(x => x.keyword == CmdTopicPrivate) > -1
                        ? TopicListFilterVisibility.Private
                        : null
            };
        }
    }

    ///
    private renderAdvFilter(): JSX.Element {
        const canAdd = Session.hasRole(SystemRoles.TopicCreator | SystemRoles.TopicManager);

        return (
            <MuiFilterAppBar position="static">
                <MuiFilterToolbar variant="dense" >
                    <MuiFilterLine>
                        {
                            canAdd &&
                            <div>
                                <IconButtonAdd
                                    color="inherit"
                                    title={this.res("ButtonNewTopicText")}
                                    onClick={() => this.handleEdit("create")}
                                />
                                <MuiClickable onClick={() => this.handleEdit("create")}>
                                    <Typography variant="button">{this.res("ButtonNewTopicText")}</Typography>
                                </MuiClickable>
                            </div>
                        }

                        <MuiPushRight>
                            <IconButtonHierarchy
                                color="inherit"
                                title={this.props.t("Views.Topic.Common.Actions.ViewHierarchy")}
                                onClick={() => this.handleTree(null)}
                            />
                            <IconButtonOrderBy
                                color="inherit"
                                onClick={
                                    (e) => {
                                        this.setState({ orderByMenuAnchorEl: e.currentTarget });
                                    }
                                }
                            />
                            <Menu
                                id="simple-menu"
                                anchorEl={this.state.orderByMenuAnchorEl}
                                open={this.state.orderByMenuAnchorEl != null}
                                onClose={
                                    () => {
                                        this.setState({ orderByMenuAnchorEl: null });
                                    }}
                            >
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.Name")} value={TopicListAdminOrderBy.Title} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MorePublications")} value={TopicListAdminOrderBy.Publications} currentSelection={this.orderBy} onClick={this.applyOrder} />
                                <MuiMenuItemChecked text={this.props.t("Common.Filter.Toolbar.OrderBy.MoreFollowers")} value={TopicListAdminOrderBy.Followers} currentSelection={this.orderBy} onClick={this.applyOrder} />
                            </Menu>
                        </MuiPushRight>
                    </MuiFilterLine>

                    <MuiFilterAdvancedContainer disableMargins>
                        <AdvFilter
                            id="advfilter"
                            commands={this.getCommands()}
                            innerRef={(ref) => this.advFilterRef = ref}
                            selectedCommands={this.state.advFilters}
                            placeHolder={this.props.t("Common.Filter.Adv.InputPlaceholder")}
                            placeHolderFocus={this.props.t("Common.Filter.Adv.InputPlaceholderFocus")}
                            title={this.props.t("Common.Filter.Adv.InputTitleEnterToFilter")}
                            onUpdateSelection={this.handleUpdateFilter}
                        />
                    </MuiFilterAdvancedContainer>
                </MuiFilterToolbar>
            </MuiFilterAppBar>
        );
    }

    ///
    private applyOrder = (orderBy: TopicListAdminOrderBy) => {
        this.orderBy = orderBy;
        this.loadData(0);
    };

    ///
    private handleUpdateFilter = (searchText: string, advFilters: AdvFilterResult[]) => {
        this.setState({ searchText, advFilters }, () => this.loadData(0));
    };

    //#endregion

    ///
    private handleEdit = (topicId: string) => {
        this.setState({ editTopicId: topicId });
    };

    ///
    private handleDeleteFromDiagram = (topicId: string): Promise<boolean> => {
        return new Promise<boolean>((resolve, reject) => {
            api.deleteTopic(topicId)
                .then((result) => {
                    if (result) {
                        this.props.snackbar.showMessage(this.res("DeleteSuccess"), "success");
                        this.loadData(this.state.offset);
                    }
                    else this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
                    resolve(result);
                })
                .catch((error) => {
                    this.setState({ lastError: error, formCycle: RequestLifeCycle.PostFailed });
                    reject(error);
                });
        });
    };

    ///
    private handleViewFeed(topicId: string) {
        HtmlUtils.openUrlInNewWindow(RoutePaths.TopicViewer.replace(":id", topicId));
    }

    ///
    private handleTree(topic: TopicListAdminItemDto) {
        this.setState({
            dialog: (
                <TopicTreeDialog
                    rootId={topic?.topicId}
                    rootName={topic?.name}
                    onEdit={this.handleEdit}
                    onDelete={this.handleDeleteFromDiagram}
                    onClose={
                        () => {
                            this.setState({ dialog: null });
                        }
                    }
                />
            )
        });
    }

    ///
    private handleJoin(topic: TopicListAdminItemDto) {

        this.setState({
            dialog: (
                <TopicJoinDialog
                    sourceTopicId={topic.topicId}
                    onClose={
                        (destinationTopic) => {

                            if (destinationTopic != undefined && destinationTopic !== null) {

                                this.setState({
                                    dialog: (
                                        <MuiConfirmation
                                            maxWidth="sm"
                                            message={
                                                <div>
                                                    <p>
                                                        <Trans
                                                            i18nKey="Views.Topic.TopicAdminList.JoinMessage"
                                                            components={
                                                                [
                                                                    <MuiDanger>source</MuiDanger>,
                                                                    <i></i>,
                                                                    <MuiSuccess>destination</MuiSuccess>,
                                                                ]
                                                            }
                                                            values={
                                                                {
                                                                    sourceName: topic.name,
                                                                    destinationName: destinationTopic.name
                                                                }
                                                            }
                                                        />
                                                    </p>
                                                    <p>{this.res("ConfirmationContinueMessage")}</p>
                                                </div>
                                            }
                                            onClose={
                                                (confirm) => {
                                                    if (confirm) {

                                                        this.setState({ dialog: null, formCycle: RequestLifeCycle.Posting });

                                                        api.joinTopics(topic.topicId, destinationTopic.id)
                                                            .then((result) => {
                                                                if (result) {
                                                                    this.props.snackbar.showMessage(this.res("TopicJoinSuccess"), "success");
                                                                    this.loadData(this.state.offset);
                                                                }
                                                                else this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
                                                            })
                                                            .catch((error) => {
                                                                this.setState({ dialog: null, lastError: error, formCycle: RequestLifeCycle.PostFailed });
                                                            });
                                                    }
                                                    else {
                                                        this.setState({ dialog: null });
                                                    }
                                                }
                                            }
                                        />
                                    )
                                });

                            }
                            else {
                                this.setState({ dialog: null });
                            }

                        }
                    }
                />
            )
        });
    }

    ///
    private handleDelete(topic: TopicListAdminItemDto) {

        if (topic.publications == 0) {
            this.setState({
                dialog: (
                    <MuiConfirmation
                        title={this.props.t("Common.Buttons.DeleteText")}
                        message={this.props.t("Common.Question.ConfirmationDeleteOfMessage", { name: topic.name })}
                        onClose={
                            (result) => {

                                if (result) {

                                    this.setState({ dialog: null, formCycle: RequestLifeCycle.Posting });

                                    api.deleteTopic(topic.topicId)
                                        .then((result) => {
                                            if (result) {
                                                this.props.snackbar.showMessage(this.res("DeleteSuccess"), "success");
                                                this.loadData(this.state.offset);
                                            }
                                            else this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
                                        })
                                        .catch((error) => {
                                            this.setState({ dialog: null, lastError: error, formCycle: RequestLifeCycle.PostFailed });
                                        });
                                }
                                else {
                                    this.setState({ dialog: null });
                                }

                            }
                        }
                    />
                )
            });
        }
        else {
            this.props.snackbar.showMessage(this.res("DeleteError"), "error");
        }
    }

    ///
    private handleRelationships(topic: TopicListAdminItemDto) {
        this.setState({
            dialog: <TopicRelationshipsDialog
                topicId={topic.topicId}
                topicName={topic.name}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    }

    ///
    private renderItems() {
        const { list } = this.state;

        if (list.total == 0 && !this.isFiltered)
            return Session.hasRole(SystemRoles.TopicCreator) && !Session.hasRole(SystemRoles.TopicManager) && !Session.hasDynamicRole(DynamicSystemRoles.TopicAdminDelegation)
                ? <MuiMessageBox variant="info" message={this.res("EmptyDataForTopicCreator")} />
                : <MuiMessageBox variant="info" message={this.res("RsResultText_zero")} />;

        if (list.total == 0 && this.isFiltered)
            return <MuiMessageBox
                variant="warning"
                action={<ButtonResetFilter color="warning" onClick={() => this.advFilterRef.resetFilter()} />}
                message={this.res("RsFilteredResultText_zero")}
            />;

        return (
            <div>
                <MuiContainer margin="normal">
                    <Grid container justifyContent="center" alignItems="center">
                        <Grid item xs>

                            <CountFilter
                                count={list.total}
                                filtered={this.isFiltered}
                                resource="Views.Topic.TopicAdminList.RsResultText"
                                filteredResource="Views.Topic.TopicAdminList.RsFilteredResultText"
                                onResetFilter={() => this.advFilterRef.resetFilter()}
                            />
                        </Grid>
                        {
                            Session.hasRole(SystemRoles.TopicCreator) && !Session.hasRole(SystemRoles.TopicManager) && !Session.hasDynamicRole(DynamicSystemRoles.TopicAdminDelegation) &&
                            <Grid item>
                                <MuiWarning showIcon>{this.res("TopicCreatorListInfo")}</MuiWarning>
                            </Grid>
                        }
                    </Grid>
                </MuiContainer>

                {
                    list.items.map((item, index) => (
                        <MuiContainer margin="dense" key={index} >
                            <TopicCard
                                topic={item}
                                key={index}
                                onEdit={() => this.handleEdit(item.topicId)}
                                onDelete={() => this.handleDelete(item)}
                                onJoin={() => this.handleJoin(item)}
                                onRelationships={() => this.handleRelationships(item)}
                                onViewFeed={() => this.handleViewFeed(item.topicId)}
                                onViewHierarchy={() => this.handleTree(item)}
                                classes={this.props.classes}
                            />
                        </MuiContainer>
                    ))
                }
                <MuiPagination
                    limit={DefaultListParameters.viewRecordsByPage}
                    offset={this.state.offset}
                    total={this.state.list.total}
                    onClick={(e, offset) => this.loadData(offset)}
                />
            </div>
        );
    }

    ///
    private renderList() {
        const { lastError, formCycle } = this.state;

        if (formCycle == RequestLifeCycle.LoadFailed)
            return <RequestErrorReport error={lastError} />;

        return (
            <>
                <RequestIndicator
                    open={formCycle == RequestLifeCycle.Posting}
                    type="post"
                />

                <RequestErrorDialog
                    open={formCycle == RequestLifeCycle.PostFailed}
                    error={lastError}
                    onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                />


                <MuiContainer margin="normal">
                    {this.renderAdvFilter()}
                </MuiContainer>

                {
                    formCycle == RequestLifeCycle.Loading
                        ? <LoadingIndicator />
                        : this.renderItems()
                }
            </>
        );
    }

    ///
    private checkForUpdateState(prevProps: Props) {
        if (this.props.match.params.id != prevProps.match.params.id || this.props.location.state?.reloadKey != prevProps.location.state?.reloadKey) {
            this.setState({ editTopicId: null });
            this.loadData(0);
        }
    }

    ///
    public componentDidMount() {
        this.loadData(0);
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        this.checkForUpdateState(prevProps);
    }

    ///
    public render() {
        const { editTopicId, dialog } = this.state;

        if (editTopicId != null)
            return <TopicEdit
                topicId={editTopicId}
                onClose={
                    (result) => {
                        if (this.props.match.params.id)
                            this.props.history.replace(LibUtils.removeRouterOptionalParameters(RoutePaths.TopicAdminList));
                        else if (result != undefined || this.state.list.items == null)
                            this.setState({ editTopicId: null }, () => this.loadData(this.state.offset));
                        else
                            this.setState({ editTopicId: null });
                    }
                }
            />;

        return (
            <div className={this.props.classes.timelineContainer} >
                { dialog}
                { this.renderList()}
            </div>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

const withAllStyles = withStyles(
    (theme) => ({
        ...TimelineStyleClasses(theme),
        ...FeedStyleClasses(theme),
    }),
    { withTheme: true },
);


export default withRouter(withSnackbar(withTranslation()(withAllStyles(TopicAdminList))));